/**
 * Authenticated AppShell wrapper (TASK-103a, + Wave 2.10 PlayerBar slot).
 *
 * Hosts Topbar + role-aware Sidebar + main content slot driven by the nested
 * `<Outlet />`. On mobile (`useResponsive().isMobile`) the navbar collapses
 * behind a burger; the rest of the time it stays open at 280 px.
 *
 * The global `<PlayerBar />` is rendered here so it stays visible across
 * route transitions inside the auth-guarded subtree. The bar itself short-
 * circuits to `null` when no track is active. When visible it occupies the
 * bottom 64 px of the viewport; `Main` gets a matching `pb` so trailing
 * content is not hidden behind it.
 *
 * Mantine v7 `AppShell` API is used directly — no extra abstraction. Mobile
 * read-only mode is enforced inside child pages via `<ReadOnlyOnMobile>`.
 */

import { useCallback, useEffect, useState } from 'react';
import { AppShell as MantineAppShell } from '@mantine/core';
import { useHotkeys, useLocalStorage } from '@mantine/hooks';
import { Outlet, useLocation } from 'react-router-dom';
import { CommandPalette } from '@/components/CommandPalette';
import { PlayerBar } from '@/components/PlayerBar';
import { usePlayer } from '@/lib/player/usePlayer';
import { useGlobalFileDropGuard } from '@/lib/use-global-file-drop-guard';
import {
  useNotificationsLiveSubscribe,
  useSystemAlertsSubscribe,
} from '@/lib/socket-subscribes';
import { Topbar } from './Topbar';
import { Sidebar } from './Sidebar';

const PLAYER_BAR_HEIGHT = 64;
/** Sidebar widths in desktop mode (mobile uses the burger drawer overlay). */
const NAVBAR_WIDTH_EXPANDED = 280;
const NAVBAR_WIDTH_COLLAPSED = 64;
/** localStorage key for the persisted collapse state. */
export const SIDEBAR_COLLAPSED_STORAGE_KEY = 'sidebar.collapsed';

export function AppShell() {
  const [mobileOpened, setMobileOpened] = useState(false);
  const [paletteOpened, setPaletteOpened] = useState(false);
  // Desktop-only collapse state. The mobile drawer keeps its own
  // `mobileOpened` toggle below; collapsing has no meaning at < 768 px where
  // the navbar is overlay-only.
  const [collapsed, setCollapsed] = useLocalStorage<boolean>({
    key: SIDEBAR_COLLAPSED_STORAGE_KEY,
    defaultValue: false,
    getInitialValueInEffect: false,
  });
  const location = useLocation();
  const { currentTrack } = usePlayer();

  // Wave 2.38 — WebSocket subscriptions live at the auth-guarded shell so
  // they are active on every protected route and torn down on logout. The
  // 30s/60s polling in the notification drawer/topbar remains as a fallback
  // when the WS connection is unavailable.
  useNotificationsLiveSubscribe();
  useSystemAlertsSubscribe();

  // BUG-168: suppress the browser's default "open the dropped file" action for
  // OS file drops that miss an explicit Dropzone (e.g. onto the catalog list).
  useGlobalFileDropGuard();

  // Close mobile drawer on navigation so the user lands on a clean view.
  useEffect(() => {
    if (mobileOpened) setMobileOpened(false);
    // We intentionally only react to pathname/search changes here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const openPalette = useCallback(() => setPaletteOpened(true), []);
  const closePalette = useCallback(() => setPaletteOpened(false), []);

  // ⌘K (Mac) / Ctrl+K (Windows/Linux) — global toggle, scoped to the
  // authenticated shell. `mod+K` resolves to the platform's primary modifier.
  useHotkeys([['mod+K', () => setPaletteOpened((v) => !v)]]);

  return (
    <MantineAppShell
      header={{ height: 60 }}
      navbar={{
        width: collapsed ? NAVBAR_WIDTH_COLLAPSED : NAVBAR_WIDTH_EXPANDED,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened, desktop: false },
      }}
      padding="md"
    >
      <MantineAppShell.Header>
        <Topbar
          mobileOpened={mobileOpened}
          onMobileToggle={() => setMobileOpened((v) => !v)}
          onOpenCommandPalette={openPalette}
          sidebarCollapsed={collapsed}
          onSidebarToggle={() => setCollapsed((v) => !v)}
        />
      </MantineAppShell.Header>

      <MantineAppShell.Navbar p={0}>
        <Sidebar collapsed={collapsed} />
      </MantineAppShell.Navbar>

      <MantineAppShell.Main
        style={{
          paddingBottom: currentTrack
            ? `calc(var(--mantine-spacing-md) + ${PLAYER_BAR_HEIGHT}px)`
            : undefined,
        }}
      >
        <Outlet />
      </MantineAppShell.Main>

      <PlayerBar />

      <CommandPalette opened={paletteOpened} onClose={closePalette} />
    </MantineAppShell>
  );
}
